/**
 * 상자(<보기>·조건 상자) 규칙 — 서버 변환기(services/lexical_convert.py, dev 1e4eb15·a70b53a)를 옮긴 것.
 *
 * 쓰는 곳 둘: OCR 검수의 원문 보기(BlockRenderer — F3)와 변형·생성 검수의 미리 보기(payloadToLexical — F4).
 * 둘 다 **그리기 전용**이라 서버 결과와 어긋나도 데이터는 안전하지만, 같은 문제가 검수 화면과 문제은행에서
 * 다른 얼굴이면 검수가 뜻을 잃는다. 그래서 규칙을 한 곳에 두고 서버와 같은 이름·같은 순서로 적는다.
 *
 *   boxMarkerPrefix — 마커가 이미 구두점(. ) ] > :)으로 끝나면 공백만, 아니면 「. 」(서버 box_marker_prefix — 대장 B5 「ㄱ..」)
 *   boxGroups       — 구형({marker, content} 항목들만)은 한 상자로, 신형은 항목 하나가 상자 하나(서버 box_groups)
 *   boxAnchor       — 최상위 상자를 「다음/아래 …다.」 도입 문장 바로 뒤로 옮길 자리(서버 box_anchor — 대장 B9).
 *                     <보기>(ㄱㄴㄷ)형·상자 여러 개·도입 뒤가 수식/표/그림·그림 순번이 꼬이는 경우는 옮기지 않는다
 * 발문 안 상자 블록 모양: { kind:'box', marker?, content?:Block[], items?:[{marker, content}] } (대장 B1 — prod 24건)
 */

const isObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const kindOf = (b) => String(b?.kind ?? b?.type ?? '');

export function boxMarkerPrefix(marker) {
  if (marker == null) return null;
  const m = String(marker).trim();
  if (!m) return null;
  return '.)]>:'.includes(m[m.length - 1]) ? `${m} ` : `${m}. `;
}

export function boxGroups(boxItems) {
  const items = (Array.isArray(boxItems) ? boxItems : []).filter(isObj);
  if (items.length === 0) return [];
  const legacyMarkerOnly = items.every((bx) => !('items' in bx) && bx.marker);
  return legacyMarkerOnly ? [items] : items.map((bx) => [bx]);
}

/** 상자 묶음 → 그릴 항목들(내용 있는 상자 자신 + 하위 items). 서버 _box_node 의 순회 순서와 같다 */
export function boxEntries(group) {
  const out = [];
  (Array.isArray(group) ? group : []).forEach((bx) => {
    if (!isObj(bx)) return;
    if (Array.isArray(bx.content) && bx.content.length > 0) out.push(bx);
    (Array.isArray(bx.items) ? bx.items : []).forEach((it) => { if (isObj(it)) out.push(it); });
  });
  return out;
}

function runsOfBlock(block) {
  if (Array.isArray(block?.runs)) return block.runs;
  if (Array.isArray(block?.content)) return block.content;
  if (Array.isArray(block?.children)) return block.children;
  return [];
}

/** 문단 블록의 평문(글·수식·빈칸 표지) — 서버 _plain_runs */
export function plainRuns(block) {
  return runsOfBlock(block).map((r) => {
    if (!isObj(r)) return '';
    const k = String(r.kind ?? r.type ?? r.t ?? '');
    if (k === 'text') return String(r.value ?? r.v ?? r.text ?? '');
    if (k === 'math') return String(r.latex ?? r.v ?? r.value ?? '');
    if (k === 'blank') return String(r.label ?? '');
    return '';
  }).join('').trim();
}

const BOGI_MARKERS = ['ㄱ', 'ㄴ', 'ㄷ', 'ㄹ'];

export function isBogiGroup(group) {
  for (const bx of (Array.isArray(group) ? group : [])) {
    if (!isObj(bx)) continue;
    const entries = [bx, ...(Array.isArray(bx.items) ? bx.items.filter(isObj) : [])];
    for (const it of entries) {
      const m = String(it.marker ?? '').trim();
      if (BOGI_MARKERS.includes(m.slice(0, 1))) return true;
      const first = (Array.isArray(it.content) ? it.content : []).find(isObj);
      if (first && BOGI_MARKERS.includes(plainRuns(first).slice(0, 1))) return true;
    }
  }
  return false;
}

export function hasImageBlock(obj) {
  if (Array.isArray(obj)) return obj.some(hasImageBlock);
  if (isObj(obj)) return obj.kind === 'image' || Object.values(obj).some(hasImageBlock);
  return false;
}

const INTRO_SENTENCE = /(다음|아래)[^.?!]*다\s*[.:]\s*$/;

/**
 * 최상위 상자를 끼울 발문 블록 위치(그 블록 **뒤**) — 옮기지 않으면 null.
 * 번호는 발문 중 객체 블록만 센 것이다(서버와 같다) — 부르는 쪽이 원래 배열 위치로 되돌린다.
 */
export function boxAnchor(stemBlocks, groups) {
  if (!Array.isArray(groups) || groups.length !== 1 || isBogiGroup(groups[0])) return null;
  const blocks = (Array.isArray(stemBlocks) ? stemBlocks : []).filter(isObj);
  if (blocks.some((b) => kindOf(b) === 'paragraph' && plainRuns(b).includes('보기'))) return null;
  for (let i = 0; i < blocks.length - 1; i += 1) {
    const b = blocks[i];
    if (kindOf(b) !== 'paragraph' || !INTRO_SENTENCE.test(plainRuns(b))) continue;
    const rest = blocks.slice(i + 1).filter((x) => !(kindOf(x) === 'paragraph' && !plainRuns(x)));
    if (rest.length === 0 || kindOf(rest[0]) !== 'paragraph') return null; // 도입 뒤가 수식·표·그림 — 그것이 '다음'의 대상
    // 상자 안과 도입 뒤 발문 양쪽에 그림이 있으면 옮기지 않는다 — 그림 번호(발문→상자→선지)와 문서 순서가 어긋난다
    if (hasImageBlock(groups[0]) && hasImageBlock(blocks.slice(i + 1))) return null;
    return i;
  }
  return null;
}
